import { Autocomplete, Chip } from "@mui/material";
import TextField from "@mui/material/TextField";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import withStyles from "@mui/styles/withStyles";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { OLModal } from "components/Tooltips/OLModal";
import { defaultOLTheme, OLTheme } from "contexts/Theme/ThemeProvider";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Field, Form, Formik, getIn, setIn } from "formik";
import { useMessage } from "hooks/intl";
import { OLButton } from "OLButton";
import React, { useState } from "react";
import { KeyValuePair } from "utilities/objects";
import * as Yup from "yup";
import { inputStylesMultiLine } from "./styles";
import { TextInput } from "./TextInput";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    modalButton: {
      marginTop: theme.spacing(12),
    },
    modalInput: {
      maxWidth: "100%",
      width: 406,
      "& + &": { marginTop: theme.spacing(10) },
    },
    input: {
      flex: 1,
      marginTop: theme.spacing(10),
      maxWidth: ({ maxWidth }: { maxWidth?: string }) => maxWidth || 373,
      width: "100%",
      [theme.breakpoints.up("md")]: {
        marginTop: theme.spacing(0),
        maxWidth: 383,
        width: "auto",
      },
    },
    nestedInput: {
      width: "100%",
      ...inputStylesMultiLine,
      "[dir='rtl'] & .MuiChip-deleteIcon": {
        margin: "0 -6px 0 5px",
      },
    },
  }),
);
const CustomAutocomplete = withStyles(() => ({
  tag: {
    borderRadius: 4,
    backgroundColor: `${defaultOLTheme.gray100} !important`,
    "& svg": {
      height: 19,
    },
  },
  endAdornment: {
    backgroundColor: `${defaultOLTheme.gray50} !important`,
    borderRadius: "0 4px 4px 0 !important",
    display: "flex",
    right: "0 !important",
    width: 56,
    height: "100%",
    "& button": {
      borderRadius: "0 4px 4px 0 !important",
      width: "100%",
    },
  },
  input: {
    "&::placeholder": {
      textOverflow: "ellipsis !important",
    },
  },
}))(Autocomplete);

export function MultiWithModal({
  "data-test-id": dataTestId,
  className,
  disableNotifications,
  formField,
  helperText,
  label,
  maxWidth,
  onlyKeyValue,
  placeholder,
  size,
  maxLength,
  disablOnMouseDown,
  onChange,
}: {
  "data-test-id"?: string;
  className?: string;
  disableNotifications?: boolean;
  formField: string;
  helperText?: string;
  label?: React.ReactNode;
  maxWidth?: string;
  onlyKeyValue?: boolean;
  placeholder?: string;
  size?: "medium" | "small";
  maxLength?: number;
  disablOnMouseDown?: boolean;
  onChange?: () => void;
}) {
  const classes = useStyles({ maxWidth });
  const { message } = useMessage();
  const [open, setOpen] = useState(false);
  const { notify } = useNotifications();

  const getOptionLabel = (option: any) =>
    onlyKeyValue
      ? `${option.key}`
      : `${option.key} ${option.value ? `: ${option.value}` : ""}`;

  return (
    <>
      <Field name={formField}>
        {({ field, form, meta }: { field: any; form: any; meta: any }) => {
          const { setFieldValue, setStatus } = form;
          const { value } = field;
          const isError = !!(meta.error || getIn(form.status, field.name));

          return (
            <>
              <CustomAutocomplete
                className={`${classes.input} ${className}`}
                disableClearable
                getOptionLabel={(option: any) => getOptionLabel(option)}
                multiple
                onChange={(_, newval) => {
                  setFieldValue(formField, newval);
                }}
                onOpen={() => setOpen(true)}
                options={value}
                popupIcon={<PlusIcon />}
                renderInput={(params) => {
                  return (
                    <TextField
                      {...params}
                      placeholder={placeholder}
                      helperText={
                        meta.error ||
                        getIn(form.status, field.name) ||
                        helperText
                      }
                      className={classes.nestedInput}
                      label={label}
                      error={isError}
                      data-test-id={`${dataTestId}-autocomplete-input`}
                      size={size}
                      slotProps={{
                        htmlInput: {
                          ...params.inputProps,
                          onMouseDown: disablOnMouseDown
                            ? () => null
                            : params.inputProps.onMouseDown,
                        },
                      }}
                    />
                  );
                }}
                renderTags={(value: any[], getTagProps) =>
                  value?.map?.((option: KeyValuePair, index: number) => (
                    <Chip
                      deleteIcon={<XCloseIcon />}
                      label={getOptionLabel(option)}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                value={value}
              />
              <OLModal
                header={message({ id: "addCustomAttribute" })}
                onClose={() => setOpen(false)}
                open={open}
                data-test-id={`${dataTestId}-modal`}
              >
                <Formik
                  initialValues={
                    onlyKeyValue ? { key: "" } : { key: "", value: "" }
                  }
                  onSubmit={async (values, { setSubmitting }) => {
                    setSubmitting(true);
                    setOpen(false);
                    setFieldValue(formField, [...value, values], true);
                    !disableNotifications &&
                      notify(message({ id: "customAttributeAdded" }));
                    setSubmitting(false);
                    // Clear field status after adding new item
                    const newStatus = setIn(form.status, formField, undefined);

                    setStatus(newStatus);
                  }}
                  validateOnBlur={false}
                  validateOnChange={false}
                  validationSchema={Yup.object().shape({
                    key: Yup.string().required(
                      message({ id: "requiredFieldError" }),
                    ),
                    value: Yup.string().when(() => {
                      if (!onlyKeyValue) {
                        return Yup.string().required(
                          message({ id: "requiredFieldError" }),
                        );
                      } else {
                        return Yup.string().notRequired();
                      }
                    }),
                  })}
                >
                  {({ setFieldValue, handleSubmit, isSubmitting }) => (
                    <Form onSubmit={handleSubmit}>
                      <TextInput
                        className={classes.modalInput}
                        label={
                          <RequiredFieldLabel text={message({ id: "key" })} />
                        }
                        name="key"
                        data-test-id={`${dataTestId}-modal-key-input`}
                        maxLength={maxLength}
                        onChange={(e) =>
                          setFieldValue(
                            "key",
                            e.target.value.trim().length > 0
                              ? e.target.value
                              : undefined,
                          )
                        }
                      />
                      {!onlyKeyValue && (
                        <TextInput
                          className={classes.modalInput}
                          label={
                            <RequiredFieldLabel
                              text={message({ id: "value" })}
                            />
                          }
                          name="value"
                          data-test-id={`${dataTestId}-modal-value-input`}
                          maxLength={maxLength}
                          onChange={(e) =>
                            setFieldValue(
                              "value",
                              e.target.value.trim().length > 0
                                ? e.target.value
                                : undefined,
                            )
                          }
                        />
                      )}
                      <OLButton
                        className={classes.modalButton}
                        color="primary"
                        disabled={isSubmitting}
                        fullWidth
                        type="button"
                        onClick={() => handleSubmit()}
                        variant="contained"
                        data-test-id={`${dataTestId}-modal-add-button`}
                      >
                        {message({ id: "addCustomAttribute" })}
                      </OLButton>
                    </Form>
                  )}
                </Formik>
              </OLModal>
            </>
          );
        }}
      </Field>
    </>
  );
}
